import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Face } from '@picsio/icons';
import { Icon } from '@picsio/ui';
import {
  func, string, number, bool, shape,
} from 'prop-types';
import Dropdown from '../../dropdown';
import { sortFaces } from '../../../helpers/faces';
import sdk from '../../../sdk';
import Logger from '../../../services/Logger';
import { getStatusFromResponceError } from '../../../shared/utils';
import ua from '../../../ua';

const FaceDropdown = (props) => {
  const {
    isFocused = false,
    faceOnAsset = {},
    faceDropdownOnClick = null,
    applyFace = null,
    assetId,
    tmpBoundingBox = null,
    addCustomFace = null,
    removeTmpBoundingBox = null,
    index,
    managePersons = false,
    imageRevisionID = '',
    removeBoundingBox = null,
  } = props;
  const [checkedItems, setCheckedItems] = useState([]);
  const [newFaceIsClicked, setNewFaceIsClicked] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const allFaces = useSelector((state) => state.faces.all);

  const [modifiedFaces, setModifiedFaces] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const processFaces = async () => {
      const result = await Promise.all(
        allFaces.map(async (face) => {
          let url = face.avatar;

          if (!url && face.isAvatarUrlReady) {
            let avatar = `/faces/${face._id}/avatar`;

            if (ua.isMobileApp()) {
              try {
                const response = await sdk.faces.getAvatarUrl(face._id);
                avatar = response.data.url;
              } catch (err) {
                const errorStatus = getStatusFromResponceError(err);
                if (errorStatus !== 409) {
                  Logger.error(new Error('Failed to load avatar in face list'), { error: err });
                }
                avatar = null;
              }
            }

            url = avatar;
          }

          return {
            ...face,
            title: face.name,
            url,
          };
        }),
      );

      if (!cancelled) {
        setModifiedFaces(result);
      }
    };

    processFaces();

    return () => {
      cancelled = true;
    };
  }, [allFaces]);

  const currentFace = useMemo(() => modifiedFaces?.filter((face) => face._id === faceOnAsset._id), [faceOnAsset._id, modifiedFaces]); // if already face was selected before
  const sortedFaces = sortFaces(modifiedFaces);

  useEffect(() => {
    setInProgress(false);
    setCheckedItems(currentFace);
  }, [currentFace, faceOnAsset]);

  const selectNewFace = (item) => {
    if (!newFaceIsClicked) {
      if (!tmpBoundingBox) {
      // apply existing face on existing bb
        applyFace({
          assetId,
          currentFaceId: faceOnAsset._id,
          newFaceId: item._id || item[0]._id,
          boundingBox: faceOnAsset.boundingBox,
          name: item.name || item[0].name,
          revisionId: imageRevisionID,
          isNewFace: false,
          unnamed: false,
        });
      } else {
      // create new face and bb
        addCustomFace({
          assetId,
          faceId: item._id || item[0]._id,
          boundingBox: {
            width: tmpBoundingBox?.boundingBox?.width,
            height: tmpBoundingBox?.boundingBox?.height,
            left: tmpBoundingBox?.boundingBox?.x,
            top: tmpBoundingBox?.boundingBox?.y,
          },
          revisionId: imageRevisionID,
          name: item.name || item[0].name,
          isNewFace: false,
        });
        removeTmpBoundingBox(index);
      }
    } else if (tmpBoundingBox) {
      removeTmpBoundingBox(index);
    }
    setNewFaceIsClicked(false);
    setCheckedItems(item.length ? item : [item]);
  };

  const handleCreateNewFace = (name) => {
    setInProgress(true);
    if (!tmpBoundingBox) {
      applyFace({
        assetId,
        currentFaceId: faceOnAsset._id,
        boundingBox: faceOnAsset.boundingBox,
        name,
        revisionId: imageRevisionID,
        isNewFace: true,
        unnamed: false,
      });
    } else {
      addCustomFace({
        assetId,
        boundingBox: {
          width: tmpBoundingBox?.boundingBox?.width,
          height: tmpBoundingBox?.boundingBox?.height,
          left: tmpBoundingBox?.boundingBox?.x,
          top: tmpBoundingBox?.boundingBox?.y,
        },
        name,
        revisionId: imageRevisionID,
        isNewFace: true,
      });
    }
    setNewFaceIsClicked(true);
  };

  const handleClickOnDropdown = () => {
    faceDropdownOnClick(faceOnAsset.boundingBox);
  };

  const handleUncheck = (faceItem) => {
    removeBoundingBox(assetId, faceItem._id, faceOnAsset.boundingBox);
  };

  return (
    <div style={{ marginBottom: '4px', border: isFocused ? '1px solid #3C86DA' : null, width: '100%' }}>
      <Dropdown
        items={sortedFaces}
        checkedItems={checkedItems}
        type="face"
        onCheckedHandler={selectNewFace}
        onDropdownOpenClickHandler={handleClickOnDropdown}
        createHandler={managePersons ? handleCreateNewFace : null}
        isOnlyCreate
        icon={() => <Icon><Face /></Icon>}
        singleChose
        filterText={allFaces.length > 0 ? 'Search persons' : 'Type person name'}
        autoFocus
        facesDropdown
        inProgress={inProgress}
        onUncheckedHandler={handleUncheck}
      />
    </div>
  );
};

FaceDropdown.propTypes = {
  assetId: string.isRequired,
  index: number.isRequired,
  isFocused: bool,
  faceOnAsset: shape({}),
  faceDropdownOnClick: func,
  applyFace: func,
  tmpBoundingBox: shape({}),
  addCustomFace: func,
  removeTmpBoundingBox: func,
  managePersons: bool,
  imageRevisionID: bool,
  removeBoundingBox: func,
};

export default FaceDropdown;
